// src/components/EmprendedorSection.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const EmprendedorSection = () => {
  const navigate = useNavigate();
  const irANuevaPantalla = () => navigate("/biografia");

  const LinkSchema = {
    "@context": "https://schema.org",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "¿Cómo usar ChatGPT para SST?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Define el objetivo (inspecciones, reportes, capacitaciones), entrega contexto de la empresa y usa prompts estructurados para generar checklists, matrices de riesgo y guiones de capacitación. Mide tiempo ahorrado y errores evitados."
        }
      },
      {
        "@type": "Question",
        "name": "Prompts ChatGPT para prevencionistas",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Usa prompts con rol, tarea, formato de salida y criterios técnicos (OSHA/ISO/Decreto 1072)."
        }
      },
      {
        "@type": "Question",
        "name": "¿Cómo automatizar informes SST con IA?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Estandariza plantillas, conecta datos, resume hallazgos con IA y genera planes de acción SMART."
        }
      },
      {
        "@type": "Question",
        "name": "IA para inspecciones de seguridad",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Visión por computador para detectar EPP, orden y señalización; dashboards por área y contratista."
        }
      }
    ]
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://ernestomora.com/" },
      { "@type": "ListItem", "position": 2, "name": "IA aplicada en SST", "item": "https://ernestomora.com/#ia-sst" }
    ]
  };

  return (
    <section id="ia-sst" className="bg-black text-white py-12 px-6 md:px-12 lg:px-20">
      <Helmet>
        <title>IA aplicada en SST | ChatGPT, herramientas y automatización — Ernesto Mora</title>
        <meta
          name="description"
          content="IA aplicada en SST para líderes HSE: cómo usar ChatGPT en seguridad laboral, prompts para prevencionistas, automatizar informes, inspecciones con IA y productividad en SST."
        />
        <meta
          name="keywords"
          content="IA aplicada en SST, ChatGPT SST, prompts ChatGPT prevencionistas, automatizar informes SST con IA, IA inspecciones seguridad, IA predictiva accidentes laborales, herramientas IA para SST, IA productividad SST, IA para profesionales HSEQ"
        />
        <meta property="og:title" content="IA aplicada en SST | Ernesto Mora" />
        <meta
          property="og:description"
          content="Transforma la seguridad laboral con IA: ChatGPT para SST, automatización de informes, inspecciones con IA y casos reales."
        />
        <meta property="og:image" content="https://celebratufiesta.com.co/wp-content/uploads/2025/01/Recurso-2.png" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://ernestomora.com/" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(LinkSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-x-12">
        {/* Columna de texto */}
        <div className="w-full md:w-1/2">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight">
            <span className="text-[#FFD700]">IA aplicada en SST</span>{" "}
            <span className="text-white">por Ernesto Mora</span>
          </h1>

          <h2 className="text-2xl font-bold mb-5 uppercase">
            ChatGPT, automatización de informes e inspecciones con IA
          </h2>

          <p className="text-gray-300 text-lg md:text-xl leading-relaxed mb-6 max-w-xl">
            Soy <strong>Ernesto Mora Soto</strong>, CEO de <strong>ESGRYMA Consulting Group</strong>.
            Ayudo a líderes HSE a <strong>aumentar productividad en SST con IA</strong>:
            <em> prompts ChatGPT para prevencionistas</em>, <em>automatización de informes SST con IA</em> e
            <em> IA para inspecciones de seguridad</em> con resultados medibles.
            También comparto estrategias prácticas en el{" "}
            {/* ✅ Aquí ya es Link */}
            <Link
              to="/blogernesto"
              className="font-bold text-[#FFD700] hover:underline"
            >
              Blog de Seguridad y Salud en el Trabajo con IA
            </Link>
            , con artículos, casos de estudio y herramientas innovadoras.
          </p>

          <ul className="text-gray-300 space-y-2 mb-6">
            <li>• <Link to="/blog/post/chatgpt-sst" className="text-[#FFD700] hover:underline">Cómo usar ChatGPT para SST</Link></li>
            <li>• <Link to="/blog/post/chatgpt-sst" className="text-[#FFD700] hover:underline">Prompts ChatGPT para prevencionistas</Link></li>
            <li>• <Link to="/blog/post/automatizacion-sst" className="text-[#FFD700] hover:underline">Automatizar informes SST con IA</Link></li>
            <li>• <Link to="/blog/post/errores-fatalas-al-implementar-inteligencia-artificial-en-seguridad-y-salud-en-el-trabajo" className="text-[#FFD700] hover:underline">IA para inspecciones de seguridad</Link></li>
            <li>• <Link to="/blog/post/7-herramientas-ia-para-sst" className="text-[#FFD700] hover:underline">Mejores herramientas IA para SST</Link></li>
            <li>• <Link to="/blog/post/ia-sst" className="text-[#FFD700] hover:underline">IA y productividad en SST (profesionales HSEQ)</Link></li>
          </ul>

          <button
            onClick={irANuevaPantalla}
            className="text-[#FFD700] text-lg font-semibold mt-2 inline-block hover:underline"
          >
            SABER MÁS…
          </button>
        </div>

        {/* Columna de imagen */}
        <motion.div
          className="w-full md:w-1/2 flex justify-center mt-10 md:mt-0"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 1 }}
        >
          <div className="relative w-full max-w-[520px]">
            <img
              src="https://celebratufiesta.com.co/wp-content/uploads/2025/01/Recurso-2.png"
              alt="IA aplicada en SST: Ernesto Mora usa ChatGPT, automatiza informes e inspecciones con IA para seguridad laboral"
              className="rounded-lg shadow-lg object-cover w-full h-auto"
              loading="lazy"
              width="520"
              height="640"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EmprendedorSection;
